import React, { useState, useEffect, useCallback } from 'react';
import cn from 'clsx';
import { Pencil, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Avatar } from 'r/ui';
import { dayjs } from 'r/utils';
import type { Analyse, ScoreMarket } from 'r/types';
import {
  getMarketPredictions,
  isMarketFrozen,
  isMarketStarted,
} from 'r/utils/scoreMarket';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { isNewUiAtom } from 'r/atoms/newUi';
import ModalBulkVerification from './ModalBulkVerification';
import ModalBulkDelete from './ModalBulkDelete';
import {
  useDeleteScoreMarket,
  useSendAnalysisToScore,
} from '../ScoreMarketsTab/mutations';

const API_AUTHOR_ID = '485b4d0f-575b-48c5-b02f-f18c8e57dba7';

interface AnalysisCardProps {
  analysis: Analyse;
  isSelected: boolean;
  selectedAnalyses: Analyse[];
  showDeletedOnly: boolean;
  isSubmitting: boolean;
  isInSanitizer: boolean;
  market: ScoreMarket | undefined;
  expandedTextId: string | null;
  getSanitizerAnalysis: (analysis: Analyse) => Analyse | undefined;
  getExistingMarket: (analysis: Analyse) => ScoreMarket | undefined;
  onToggleSelect: (analysis: Analyse) => void;
  onToggleExpand: (id: string | null) => void;
  onVerify: (analysisId: string, onSuccess?: () => void) => void;
  onAddToSanitizer: (analysis: Analyse) => void;
  onRestore: (analysis: Analyse) => void;
  onBulkSuccess: () => void;
}

export default React.memo(function AnalysisCard({
  analysis,
  isSelected,
  selectedAnalyses,
  showDeletedOnly,
  isSubmitting,
  isInSanitizer,
  market,
  expandedTextId,
  getSanitizerAnalysis,
  getExistingMarket,
  onToggleSelect,
  onToggleExpand,
  onVerify,
  onAddToSanitizer,
  onRestore,
  onBulkSuccess,
}: AnalysisCardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const isNewUi = useAtomValue(isNewUiAtom);
  const [isVerifyModalOpen, setIsVerifyModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [isConfirmingScoreDelete, setIsConfirmingScoreDelete] = useState(false);
  const { mutate: sendToScore, isPending: isSendingToScore } =
    useSendAnalysisToScore();
  const { mutate: deleteFromScore, isPending: isDeletingFromScore } =
    useDeleteScoreMarket();
  const isMarketLive = !!market && isMarketStarted(market);
  const isMarketLocked = !!market && isMarketFrozen(market);

  const images = analysis.images ?? [];

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        setLightboxIndex((i) => (i !== null ? Math.max(0, i - 1) : null));
      } else if (e.key === 'ArrowRight') {
        setLightboxIndex((i) =>
          i !== null ? Math.min(images.length - 1, i + 1) : null,
        );
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxIndex, images.length]);

  return (
    <div
      className={cn(
        'relative bg-main rounded-xl border border-bg-accent-tabs p-5 flex flex-col gap-4 hover:border-text-tertiary transition-colors',
        analysis.verifiedBy && 'border-l-4 border-l-yellow-500',
      )}
    >
      {/* Author Info */}
      <div className="flex items-center gap-3">
        <Avatar
          size={40}
          user={{
            id: analysis.authorUsername,
            avatar: analysis.authorIcon,
          }}
        />
        <div className="flex flex-col flex-1 min-w-0">
          <span className="text-main font-semibold truncate">
            {analysis.authorName}
          </span>
          <span className="text-xs text-secondary truncate">
            @{analysis.authorUsername}
          </span>
        </div>
        {!showDeletedOnly && (
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => onToggleSelect(analysis)}
            aria-label={`Select analysis by ${analysis.authorName}`}
            className={cn(analysis.verifiedBy && 'border-yellow-500')}
          />
        )}
      </div>

      {/* Status Badges */}
      <div className="flex flex-wrap items-center gap-2">
        {analysis.deletedFromTweeterAt !== null && (
          <span className="px-2.5 py-1 bg-red-500/10 text-red-500 text-xs font-medium rounded-full">
            Deleted
          </span>
        )}
        {analysis.verifiedBy && (
          <span className="px-2.5 py-1 bg-yellow-500/10 text-yellow-500 text-xs font-medium rounded-full">
            Verified
          </span>
        )}
        {analysis.verifiedBy && (
          <span className="text-yellow-600 text-xs truncate max-w-20">
            by {analysis.verifiedBy}
          </span>
        )}
        {analysis.analysisAuthorId === API_AUTHOR_ID ?
          <span className="px-2.5 py-1 bg-yellow-500/10 text-yellow-500 text-xs font-medium rounded-full">
            API
          </span>
        : <span className="px-2.5 py-1 bg-green-500/10 text-green-500 text-xs font-medium rounded-full">
            Manual
          </span>
        }
        {analysis.emergingScore !== null && (
          <span className="px-2.5 py-1 bg-red-500/10 text-red-500 text-xs font-medium rounded-full">
            Score: {parseFloat(analysis.emergingScore).toFixed(2)}
          </span>
        )}
      </div>

      {/* Analysis Text */}
      <div className="flex flex-col gap-2 text-sm text-main leading-relaxed">
        <div>
          <span className="text-xs text-secondary font-normal">
            Created: {dayjs(analysis.createdAt).format('DD.MM.YYYY')}
          </span>
          <span className="ml-3 text-xs text-secondary">
            Tweet date: {dayjs(analysis.tweetDate).format('DD.MM.YYYY HH:mm')}
          </span>
          <br />
          <a
            className={cn(
              'h-8 text-xs font-normal underline cursor-pointer',
              analysis.deletedFromTweeterAt ? 'text-(--bearish)' : 'text-focus',
            )}
            href={`https://x.com/${analysis.authorID}/status/${analysis.tweetID}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            Original {analysis.deletedFromTweeterAt ? 'deleted' : 'tweet'} link
          </a>
        </div>
        {analysis.tweetText.length > 150 ?
          <span>
            {expandedTextId === analysis.id ?
              analysis.tweetText
            : analysis.tweetText.substring(0, 150) + '...'}
            <Button
              variant="link"
              size="sm"
              onClick={() =>
                onToggleExpand(
                  expandedTextId === analysis.id ? null : analysis.id,
                )
              }
              className="ml-1 inline-block"
            >
              {expandedTextId === analysis.id ? 'Show less' : 'Show more'}
            </Button>
          </span>
        : analysis.tweetText}
        {images.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {images.map((src, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setLightboxIndex(i)}
                className="focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                aria-label={`View image ${i + 1} of ${images.length}`}
              >
                <img
                  src={src}
                  className="max-h-20 rounded cursor-pointer hover:opacity-75 transition-opacity"
                  alt={`Analysis image ${i + 1}`}
                />
              </button>
            ))}
          </div>
        )}

        {lightboxIndex !== null && (
          <Dialog open onOpenChange={closeLightbox}>
            <DialogContent
              hideCloseButton
              className="max-w-5xl w-full bg-white border border-border p-0 overflow-hidden shadow-xl"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-white">
                {images.length > 1 ?
                  <span className="text-gray-500 text-sm select-none">
                    {lightboxIndex + 1} / {images.length}
                  </span>
                : <span />}
                <button
                  type="button"
                  onClick={closeLightbox}
                  className="text-gray-400 hover:text-gray-700 transition-colors"
                  aria-label="Close image viewer"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Image area */}
              <div className="relative flex items-center justify-center p-6 bg-gray-50">
                <img
                  src={images[lightboxIndex]}
                  className="max-h-[75vh] max-w-full object-contain rounded"
                  alt={`Analysis image ${lightboxIndex + 1}`}
                />

                {/* Prev / Next */}
                {images.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={() =>
                        setLightboxIndex((i) =>
                          i !== null ? Math.max(0, i - 1) : null,
                        )
                      }
                      disabled={lightboxIndex === 0}
                      className="absolute left-2 text-gray-400 hover:text-gray-700 disabled:opacity-20 transition-colors"
                      aria-label="Previous image"
                    >
                      <ChevronLeft className="h-10 w-10" />
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setLightboxIndex((i) =>
                          i !== null ?
                            Math.min(images.length - 1, i + 1)
                          : null,
                        )
                      }
                      disabled={lightboxIndex === images.length - 1}
                      className="absolute right-2 text-gray-400 hover:text-gray-700 disabled:opacity-20 transition-colors"
                      aria-label="Next image"
                    >
                      <ChevronRight className="h-10 w-10" />
                    </button>
                  </>
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Card Footer with Controls */}
      {(isSelected || selectedAnalyses.length === 0) && (
        <div className="mt-auto pt-4 border-t border-bg-accent-tabs flex flex-col gap-3">
          {/* Info Chips */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="px-3 py-1 bg-bg-accent-main text-main text-xs font-semibold uppercase tracking-wide rounded-md border border-bg-accent-tabs">
              {analysis.pair}
            </span>
            <span
              className={cn(
                'px-3 py-1 text-xs font-bold rounded-md uppercase tracking-wide',
                analysis.isPredictionUp ?
                  'bg-green-500/10 text-green-500 border border-green-500/20'
                : 'bg-red-500/10 text-red-500 border border-red-500/20',
              )}
            >
              {analysis.isPredictionUp ? 'Up' : 'Down'}
            </span>
            <Button
              disabled={showDeletedOnly}
              variant="outline"
              size="icon"
              aria-label="Edit analysis"
              onClick={() =>
                navigate(`/analyses/${analysis.id}/edit`, {
                  state: { background: location },
                })
              }
            >
              <Pencil className="h-4 w-4" />
            </Button>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (isInSanitizer) return;
                if (!analysis.verifiedBy) {
                  onVerify(analysis.id, () => onAddToSanitizer(analysis));
                } else {
                  onAddToSanitizer(analysis);
                }
              }}
              // In the new UI the Sanitizer is deprioritized: adding is turned off
              disabled={isNewUi || showDeletedOnly || isInSanitizer}
              title={isNewUi ? 'Sanitizer is disabled' : undefined}
              className={cn('text-xs', isInSanitizer && 'opacity-60')}
            >
              <span className="m-auto">
                {isInSanitizer ? 'In Sanitizer' : 'Add to Sanitizer'}
              </span>
            </Button>
            <Button
              variant={market ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                const dest = `/analyses/${analysis.id}/score-market`;
                const navState = { state: { background: location } };
                if (!analysis.verifiedBy) {
                  onVerify(analysis.id, () => navigate(dest, navState));
                } else {
                  navigate(dest, navState);
                }
              }}
              disabled={showDeletedOnly}
              className="text-xs"
            >
              <span className="m-auto">
                {market ? 'Score created' : 'Add to Score'}
              </span>
            </Button>
            {market && !showDeletedOnly && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => sendToScore({ analysis, market })}
                  disabled={isSendingToScore}
                  className="text-xs"
                >
                  <span className="m-auto">Send update to Score</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (!isConfirmingScoreDelete) {
                      setIsConfirmingScoreDelete(true);
                      return;
                    }
                    deleteFromScore(market.id);
                  }}
                  onBlur={() => setIsConfirmingScoreDelete(false)}
                  disabled={isMarketLive || isDeletingFromScore}
                  title={
                    isMarketLive ?
                      'Started markets cannot be deleted'
                    : undefined
                  }
                  className="text-xs text-red-500"
                >
                  <span className="m-auto">
                    {isConfirmingScoreDelete ?
                      'Confirm delete'
                    : 'Delete from Score'}
                  </span>
                </Button>
                <p className="col-span-2 text-xs text-secondary">
                  {getMarketPredictions(market) > 0 ?
                    `Score: ${getMarketPredictions(market)} predictions`
                  : 'Score: no predictions'}
                  {isMarketLocked ?
                    ' · KOL/pair/direction locked'
                  : ' · fully editable'}
                  {isMarketLive && ' · started, cannot be deleted'}
                </p>
              </>
            )}
            {analysis.deletedAt === null ?
              <>
                {selectedAnalyses.length > 1 ?
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsVerifyModalOpen(true)}
                    >
                      <span className="m-auto">
                        Verify ({selectedAnalyses.length})
                      </span>
                    </Button>
                    {isVerifyModalOpen && (
                      <ModalBulkVerification
                        analyses={selectedAnalyses}
                        onSuccess={() => {
                          setIsVerifyModalOpen(false);
                          onBulkSuccess();
                        }}
                      />
                    )}
                  </>
                : <Button
                    variant="default"
                    size="sm"
                    onClick={() => onVerify(analysis.id)}
                    disabled={isSubmitting || !!analysis.verifiedBy}
                    className="text-xs"
                  >
                    <span className="m-auto">Verify</span>
                  </Button>
                }
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setIsDeleteModalOpen(true)}
                >
                  <span className="m-auto">
                    Delete
                    {selectedAnalyses.length > 1 &&
                      ` (${selectedAnalyses.length})`}
                  </span>
                </Button>
                {isDeleteModalOpen && (
                  <ModalBulkDelete
                    analyses={
                      selectedAnalyses.length > 1 ?
                        selectedAnalyses
                      : [analysis]
                    }
                    getSanitizerAnalysis={getSanitizerAnalysis}
                    getExistingMarket={getExistingMarket}
                    onSuccess={() => {
                      setIsDeleteModalOpen(false);
                      onBulkSuccess();
                    }}
                  />
                )}
              </>
            : <Button
                variant="default"
                size="sm"
                onClick={() => onRestore(analysis)}
                disabled={isSubmitting}
                className="text-xs"
              >
                <span className="m-auto">Restore</span>
              </Button>
            }
          </div>
        </div>
      )}
    </div>
  );
});
